/**
 * DashboardPage
 *
 * Company-wide overview -- fully real now that both SSS and TRS are
 * confirmed ready. Reuses `computeComparisonRows()`/`summarizeRows()`
 * from useWipComparison.js (the same staff+engagement-level join logic
 * the Variance Matrix page uses) against a wide, company-wide fetch
 * (no cut-off-period narrowing, unlike that page) for the summary cards,
 * status-mix pie chart, and "Top Engagements by Cost Variance" list.
 * The weekly variance-trend line chart buckets the RAW schedule/time-entry
 * records by week directly (computeComparisonRows aggregates away
 * per-date granularity, which this chart specifically needs).
 *
 * Falls back to sample data (src/data/sampleDashboardData.js) if the
 * real SSS fetch errors or returns nothing; if SSS is fine but TRS
 * specifically is unreachable, the scheduled-hours-only figures (top
 * card, branch bar chart) still show, but variance/cost/status-mix/trend
 * figures fall back to "Pending TRS"/sample rather than a wrong number.
 */
import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AlertTriangle, Clock, DollarSign, TrendingUp } from 'lucide-react';
import { useBranchContext } from '../hooks/useBranchContext.js';
import { useEngagements, useClients, useStaffList, usePositionRates, useSchedules, scheduleEngagementId, scheduleHours } from '../hooks/useReferenceData.js';
import { useTimeEntries, timeEntryDate, timeEntryHours, isChargeableClientEntry } from '../hooks/useTrsTimeEntries.js';
import { computeComparisonRows, summarizeRows } from '../hooks/useWipComparison.js';
import { sampleStatusBreakdown, sampleTopEngagements, sampleVarianceTrend } from '../data/sampleDashboardData.js';

const currency = (n) =>
  n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

/** Shared tooltip/axis styling so every chart matches the current theme. */
const axisTickStyle = { fill: 'rgb(var(--c-text-muted))', fontSize: 12 };
const tooltipStyle = {
  backgroundColor: 'rgb(var(--c-bg-surface))',
  border: '1px solid rgb(var(--c-border))',
  borderRadius: 8,
  fontSize: 12,
  color: 'rgb(var(--c-text-primary))',
};

const STATUS_CHART_META = {
  on_track: { label: 'On-Track', color: '#34d399' },
  over_budget: { label: 'Over-Budget', color: '#fbbf24' },
  unscheduled: { label: 'Unscheduled', color: '#fb7185' },
  no_show: { label: 'No-Show', color: '#94a3b8' },
};

const WEEKS_BACK = 6;

/**
 * Buckets raw schedules/chargeable time entries into the last N
 * (Sunday-start) weeks, computing each week's net hour variance
 * (actual - scheduled) directly from dated records -- separate from
 * computeComparisonRows(), which aggregates away per-date granularity.
 * @param {any[]} schedules
 * @param {any[]} timeEntries
 */
function buildWeeklyVarianceTrend(schedules, timeEntries) {
  const now = new Date();
  const weeks = [];
  for (let i = WEEKS_BACK - 1; i >= 0; i--) {
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - start.getDay() - i * 7);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    weeks.push({ start, end });
  }

  return weeks.map((w, idx) => {
    let scheduled = 0;
    for (const s of schedules) {
      const d = new Date(s.start_date ?? s.date ?? s.created_at);
      if (!Number.isNaN(d.getTime()) && d >= w.start && d <= w.end) scheduled += scheduleHours(s);
    }
    let actual = 0;
    for (const t of timeEntries) {
      const d = new Date(timeEntryDate(t));
      if (!Number.isNaN(d.getTime()) && d >= w.start && d <= w.end) actual += timeEntryHours(t);
    }
    return { week: `W${idx + 1}`, variance: Math.round((actual - scheduled) * 100) / 100 };
  });
}

/**
 * @param {{ label: string, value: string, icon: import('react').ComponentType<any>, tone?: 'default'|'amber'|'blue'|'warning'|'pending' }} props
 */
const MetricCard = ({ label, value, icon: Icon, tone = 'default' }) => {
  const toneClasses = {
    default: 'text-primary',
    amber: 'text-amber-500',
    blue: 'text-sky-500',
    warning: 'text-rose-500',
    pending: 'text-muted',
  }[tone];

  return (
    <div className="flex flex-1 min-w-[200px] items-start justify-between rounded-lg border border-edge bg-surface p-4 shadow-sm">
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-muted">{label}</p>
        <p className={`mt-2 text-2xl font-semibold ${toneClasses}`}>{value}</p>
      </div>
      <Icon className={`h-5 w-5 ${toneClasses}`} aria-hidden="true" />
    </div>
  );
};

export const DashboardPage = () => {
  const { branches } = useBranchContext();
  // Company-wide overview -- deliberately NOT scoped to the topbar's
  // selected branch, unlike the other pages.
  const schedulesQuery = useSchedules({});
  const engagementsQuery = useEngagements();
  const clientsQuery = useClients();
  const staffQuery = useStaffList();
  const positionRatesQuery = usePositionRates();
  const timeEntriesQuery = useTimeEntries();

  const isLoading =
    schedulesQuery.isLoading || engagementsQuery.isLoading || clientsQuery.isLoading ||
    staffQuery.isLoading || positionRatesQuery.isLoading;
  const isError =
    schedulesQuery.isError || engagementsQuery.isError || clientsQuery.isError ||
    staffQuery.isError || positionRatesQuery.isError;
  const usingSampleData = !isLoading && (isError || (schedulesQuery.data ?? []).length === 0);
  // TRS excluded from the gates above -- same reasoning as the other
  // pages: a TRS hiccup shouldn't take down the SSS-driven figures.
  const trsDataReady = !timeEntriesQuery.isError;

  const totalScheduledHours = useMemo(() => {
    if (usingSampleData) return null;
    return Math.round((schedulesQuery.data ?? []).reduce((sum, s) => sum + scheduleHours(s), 0) * 100) / 100;
  }, [usingSampleData, schedulesQuery.data]);

  const hoursByBranch = useMemo(() => {
    if (usingSampleData || branches.length === 0) return [];
    const byBranch = new Map();
    for (const s of schedulesQuery.data ?? []) {
      const branchId = s.branch_id != null ? String(s.branch_id) : null;
      if (!branchId) continue;
      byBranch.set(branchId, (byBranch.get(branchId) ?? 0) + scheduleHours(s));
    }
    return branches
      .filter((b) => byBranch.has(b.id))
      .map((b) => ({ branch: b.name, scheduled: Math.round((byBranch.get(b.id) ?? 0) * 100) / 100 }));
  }, [usingSampleData, branches, schedulesQuery.data]);

  // Company-wide comparison rows -- reuses the SAME staff+engagement join
  // logic the Variance Matrix page uses, just against a wide fetch
  // instead of one cut-off period. Only meaningful once TRS is ready;
  // with TRS down, `rows` still has real scheduled hours but actualHours
  // is silently 0 for every row -- gated by `trsDataReady` before this
  // feeds the variance/cost/status-mix figures below.
  const comparisonRows = useMemo(() => {
    if (usingSampleData || !trsDataReady) return [];
    return computeComparisonRows({
      schedules: schedulesQuery.data ?? [],
      timeEntries: (timeEntriesQuery.data ?? []).filter(isChargeableClientEntry),
      engagements: engagementsQuery.data ?? [],
      clients: clientsQuery.data ?? [],
      staff: staffQuery.data ?? [],
      positionRates: positionRatesQuery.data ?? [],
      filters: { engagementId: null, staffIds: [] },
    });
  }, [
    usingSampleData, trsDataReady, schedulesQuery.data, timeEntriesQuery.data,
    engagementsQuery.data, clientsQuery.data, staffQuery.data, positionRatesQuery.data,
  ]);

  const summary = useMemo(
    () => (comparisonRows.length ? summarizeRows(comparisonRows) : null),
    [comparisonRows]
  );

  const statusMixData = useMemo(() => {
    if (!comparisonRows.length) return [];
    const counts = new Map();
    for (const r of comparisonRows) counts.set(r.status, (counts.get(r.status) ?? 0) + 1);
    return [...counts.entries()]
      .filter(([, count]) => count > 0)
      .map(([status, count]) => ({ status: STATUS_CHART_META[status]?.label ?? status, count, color: STATUS_CHART_META[status]?.color ?? '#94a3b8' }));
  }, [comparisonRows]);

  const topEngagementsByCostVariance = useMemo(() => {
    if (!comparisonRows.length) return [];
    const byEngagement = new Map();
    for (const r of comparisonRows) {
      const existing = byEngagement.get(r.engagementId);
      if (existing) {
        existing.costVariance += r.costVariance;
      } else {
        byEngagement.set(r.engagementId, { engagement: r.engagementName, client: r.clientName, costVariance: r.costVariance });
      }
    }
    return [...byEngagement.values()]
      .sort((a, b) => Math.abs(b.costVariance) - Math.abs(a.costVariance))
      .slice(0, 5);
  }, [comparisonRows]);

  const weeklyTrend = useMemo(() => {
    if (usingSampleData || !trsDataReady) return [];
    return buildWeeklyVarianceTrend(schedulesQuery.data ?? [], (timeEntriesQuery.data ?? []).filter(isChargeableClientEntry));
  }, [usingSampleData, trsDataReady, schedulesQuery.data, timeEntriesQuery.data]);

  const topEngagementsByScheduled = useMemo(() => {
    if (usingSampleData) return [];
    const engagementById = new Map((engagementsQuery.data ?? []).map((e) => [String(e.id), e]));
    const hoursByEngagement = new Map();
    for (const s of schedulesQuery.data ?? []) {
      const engId = scheduleEngagementId(s);
      if (!engId) continue;
      hoursByEngagement.set(engId, (hoursByEngagement.get(engId) ?? 0) + scheduleHours(s));
    }
    return [...hoursByEngagement.entries()]
      .map(([engId, hours]) => ({
        engagementId: engId,
        engagementName: engagementById.get(engId)?.title ?? engagementById.get(engId)?.name ?? `Engagement #${engId}`,
        scheduledHours: Math.round(hours * 100) / 100,
      }))
      .sort((a, b) => b.scheduledHours - a.scheduledHours)
      .slice(0, 5);
  }, [usingSampleData, engagementsQuery.data, schedulesQuery.data]);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-primary">Dashboard</h1>
          <p className="text-sm text-muted">Company-wide WIP snapshot.</p>
        </div>
        {usingSampleData ? (
          <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-500">
            Sample data{isError ? ' \u2014 live fetch failed' : ' \u2014 no live schedules yet'}
          </span>
        ) : (
          <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-500">
            Live from SSS{trsDataReady ? ' & TRS' : ' \u2014 TRS unreachable, variance/cost figures pending'}
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-4">
        <MetricCard
          label="Total Scheduled Hours"
          value={usingSampleData ? '\u2014' : totalScheduledHours.toLocaleString()}
          icon={Clock}
        />
        <MetricCard
          label="Net Hour Variance"
          value={summary ? `${summary.net_hour_variance >= 0 ? '+' : ''}${summary.net_hour_variance.toLocaleString()} hrs` : 'Pending TRS'}
          icon={TrendingUp}
          tone={summary ? (summary.net_hour_variance > 0 ? 'amber' : 'blue') : 'pending'}
        />
        <MetricCard
          label="Total WIP Value Variance"
          value={summary ? currency(summary.total_wip_cost_variance) : 'Pending TRS'}
          icon={DollarSign}
          tone={summary ? (summary.total_wip_cost_variance >= 0 ? 'amber' : 'blue') : 'pending'}
        />
        <MetricCard
          label="Unscheduled Actuals"
          value={summary ? String(summary.unscheduled_actuals_count) : 'Pending TRS'}
          icon={AlertTriangle}
          tone={summary ? (summary.unscheduled_actuals_count > 0 ? 'warning' : 'default') : 'pending'}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-lg border border-edge bg-surface p-4 lg:col-span-2">
          <p className="mb-3 text-sm font-medium text-primary">Scheduled Hours by Branch</p>
          {usingSampleData ? (
            <p className="flex h-[280px] items-center justify-center text-sm text-muted">
              No live schedule data yet.
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={hoursByBranch} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--c-border))" vertical={false} />
                <XAxis dataKey="branch" tick={axisTickStyle} axisLine={{ stroke: 'rgb(var(--c-border))' }} tickLine={false} />
                <YAxis tick={axisTickStyle} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgb(var(--c-bg-surface-hover))' }} />
                <Legend wrapperStyle={{ fontSize: 12, color: 'rgb(var(--c-text-secondary))' }} />
                <Bar dataKey="scheduled" name="Scheduled Hrs" fill="#0284c7" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="rounded-lg border border-edge bg-surface p-4">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-medium text-primary">Variance Status Mix</p>
            {!statusMixData.length && (
              <span className="text-[10px] font-medium text-amber-500">Sample &middot; needs TRS</span>
            )}
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={statusMixData.length ? statusMixData : sampleStatusBreakdown}
                dataKey="count"
                nameKey="status"
                innerRadius={55}
                outerRadius={90}
                paddingAngle={2}
              >
                {(statusMixData.length ? statusMixData : sampleStatusBreakdown).map((entry) => (
                  <Cell key={entry.status} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
              <Legend
                wrapperStyle={{ fontSize: 12, color: 'rgb(var(--c-text-secondary))' }}
                layout="vertical"
                verticalAlign="middle"
                align="right"
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-lg border border-edge bg-surface p-4 lg:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-medium text-primary">Net Hour Variance Trend</p>
            {!weeklyTrend.length && (
              <span className="text-[10px] font-medium text-amber-500">Sample &middot; needs TRS</span>
            )}
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={weeklyTrend.length ? weeklyTrend : sampleVarianceTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--c-border))" vertical={false} />
              <XAxis dataKey="week" tick={axisTickStyle} axisLine={{ stroke: 'rgb(var(--c-border))' }} tickLine={false} />
              <YAxis tick={axisTickStyle} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Line
                type="monotone"
                dataKey="variance"
                name="Net Variance (Hrs)"
                stroke="#38bdf8"
                strokeWidth={2}
                dot={{ r: 3, fill: '#38bdf8' }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-lg border border-edge bg-surface p-4">
          <p className="mb-3 text-sm font-medium text-primary">
            {topEngagementsByCostVariance.length ? 'Top Engagements by Cost Variance' : 'Top Engagements by Scheduled Hours'}
          </p>
          {topEngagementsByCostVariance.length ? (
            <ul className="space-y-3">
              {topEngagementsByCostVariance.map((e) => (
                <li key={e.engagement} className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm text-primary">{e.engagement}</p>
                    <p className="truncate text-xs text-muted">{e.client}</p>
                  </div>
                  <span
                    className={`shrink-0 text-sm font-medium ${
                      e.costVariance >= 0 ? 'text-amber-500' : 'text-sky-500'
                    }`}
                  >
                    {currency(e.costVariance)}
                  </span>
                </li>
              ))}
            </ul>
          ) : usingSampleData ? (
            <ul className="space-y-3">
              {sampleTopEngagements.map((e) => (
                <li key={e.engagement} className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm text-primary">{e.engagement}</p>
                    <p className="truncate text-xs text-muted">{e.client}</p>
                  </div>
                  <span
                    className={`shrink-0 text-sm font-medium ${
                      e.costVariance >= 0 ? 'text-amber-500' : 'text-sky-500'
                    }`}
                  >
                    {currency(e.costVariance)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <ul className="space-y-3">
              {topEngagementsByScheduled.map((e) => (
                <li key={e.engagementId} className="flex items-center justify-between gap-3">
                  <p className="truncate text-sm text-primary">{e.engagementName}</p>
                  <span className="shrink-0 text-sm font-medium text-accent">
                    {e.scheduledHours.toLocaleString()} hrs
                  </span>
                </li>
              ))}
              {topEngagementsByScheduled.length === 0 && (
                <p className="text-sm text-muted">No scheduled hours yet.</p>
              )}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};
